import { useMemo } from "react";
import { Blocks } from "./Blocks";
import { PresetShell } from "./PresetShell";
import {
  compactBlocks,
  createAtom,
  readObjects,
  readString,
  readStringArray,
} from "./data";
import type { PresetViewProps } from "./CourseOverview";

/**
 * Un concepto a fondo: definición, ecuaciones, lo que hay que llevarse y las
 * confusiones típicas.
 *
 * Las confusiones no son un anexo: en este curso son la mitad del aprendizaje
 * (confundir CTN con NOF, descontar el FCL a la tasa de interés en vez del
 * WACC), así que van en su propio destacado y no escondidas.
 */
export function ConceptDeepDive({ preset, data, emit, interactive }: PresetViewProps) {
  const concept = readString(data, "concept") ?? preset.title ?? "Concepto";
  const module = readString(data, "module");
  const definition = readString(data, "definition");
  const keyPoints = readStringArray(data, "key_points");
  const misconceptions = readStringArray(data, "misconceptions");
  const related = readStringArray(data, "related");

  const formulas = useMemo(
    () =>
      readObjects(data, "formulas")
        .map((formula, index) => ({
          name: readString(formula, "name") ?? `Fórmula ${index + 1}`,
          latex: readString(formula, "latex") ?? "",
        }))
        .filter((formula) => formula.latex !== ""),
    [data],
  );

  const blocks = compactBlocks([
    definition
      ? createAtom("prose", {
          markdown: definition,
        })
      : null,

    ...formulas.map((formula) =>
      createAtom("formula", {
        latex: formula.latex,
        caption: formula.name,
      }),
    ),

    keyPoints.length > 0
      ? createAtom("callout", {
          variant: "key-idea",
          title: "Lo que hay que llevarse",
          body: keyPoints.map((point) => `• ${point}`).join("\n\n"),
        })
      : null,

    misconceptions.length > 0
      ? createAtom("callout", {
          variant: "common-error",
          title: "Confusiones típicas",
          body: misconceptions.map((item) => `• ${item}`).join("\n\n"),
        })
      : null,

    createAtom("callout", {
      variant: "outside-syllabus",
      body: `Este desarrollo amplía lo que cubre el sílabo del curso. Para la evaluación, quédate con la notación y los criterios del material del curso${module ? ` (${module})` : ""}.`,
    }),
  ]);

  return (
    <PresetShell
      eyebrow={module ? `${module} · concepto` : "Concepto"}
      title={preset.title ?? concept}
      lead={definition}
      chips={[
        ...(module ? [{ icon: "layers" as const, label: module }] : []),
        ...(formulas.length > 0
          ? [{ icon: "sigma" as const, label: `${formulas.length} ${formulas.length === 1 ? "ecuación" : "ecuaciones"}` }]
          : []),
        ...(related.length > 0
          ? [{ icon: "arrow-right" as const, label: `${related.length} conceptos relacionados` }]
          : []),
      ]}
    >
      <Blocks blocks={blocks} emit={emit} interactive={interactive} />

      {related.length > 0 ? (
        <footer className="preset__related">
          <span className="u-label">Conceptos vecinos</span>
          <ul className="preset__related-list">
            {related.map((item) => (
              <li key={item} className="preset__related-item">
                {item}
              </li>
            ))}
          </ul>
        </footer>
      ) : null}
    </PresetShell>
  );
}

export default ConceptDeepDive;
